import * as React from 'react';

import type { ToastProps } from '@/components/ui/toast';

export const TOAST_DECK_LIMIT = 5;
const TOAST_REMOVE_DELAY = 1000;
export const ERROR_TOAST_DURATION = 60_000;

export type ToasterToast = {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
} & ToastProps;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type Action =
  | {
      type: 'ADD_TOAST';
      toast: ToasterToast;
    }
  | {
      type: 'UPDATE_TOAST';
      toast: Partial<ToasterToast>;
    }
  | {
      type: 'DISMISS_TOAST';
      toastId?: ToasterToast['id'];
    }
  | {
      type: 'REMOVE_TOAST';
      toastId?: ToasterToast['id'];
    };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
const toastDismissTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

export function isErrorToast(toast: Pick<ToasterToast, 'variant'>) {
  return toast.variant === 'destructive';
}

export function hasActiveToasts(toasts: ToasterToast[]) {
  return toasts.some((item) => item.open !== false);
}

/** @deprecated use hasActiveToasts for the unified toast deck. */
export const hasActiveErrorToasts = hasActiveToasts;

export function normalizeToastDuration<T extends Partial<ToasterToast>>(
  toast: T
): T & { duration?: ToasterToast['duration'] } {
  if (!isErrorToast(toast)) return toast;
  return {
    ...toast,
    duration: toast.duration ?? ERROR_TOAST_DURATION,
  };
}

export function limitToasts(toasts: ToasterToast[]) {
  const visibleToasts = toasts.slice(0, TOAST_DECK_LIMIT);
  const evictedToasts = toasts.slice(TOAST_DECK_LIMIT);

  evictedToasts.forEach((item) => {
    clearToastTimers(item.id);
  });

  return visibleToasts;
}

function clearToastTimers(toastId: string) {
  const removeTimeout = toastTimeouts.get(toastId);
  if (removeTimeout) {
    clearTimeout(removeTimeout);
    toastTimeouts.delete(toastId);
  }

  const dismissTimeout = toastDismissTimeouts.get(toastId);
  if (dismissTimeout) {
    clearTimeout(dismissTimeout);
    toastDismissTimeouts.delete(toastId);
  }
}

function scheduleAutoDismiss(toast: ToasterToast) {
  const duration = toast.duration;
  if (typeof duration !== 'number' || !Number.isFinite(duration) || duration <= 0) return;

  const timeout = setTimeout(() => {
    toastDismissTimeouts.delete(toast.id);
    dispatch({ type: 'DISMISS_TOAST', toastId: toast.id });
  }, duration);

  toastDismissTimeouts.set(toast.id, timeout);
}

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }
  const dismissTimeout = toastDismissTimeouts.get(toastId);
  if (dismissTimeout) {
    clearTimeout(dismissTimeout);
    toastDismissTimeouts.delete(toastId);
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: 'REMOVE_TOAST',
      toastId: toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: limitToasts([normalizeToastDuration(action.toast), ...state.toasts]),
      };

    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? normalizeToastDuration({ ...t, ...action.toast }) : t
        ),
      };

    case 'DISMISS_TOAST': {
      const { toastId } = action;

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || action.toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      };
    }
    case 'REMOVE_TOAST':
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

export function getToastState() {
  return memoryState;
}

export function clearToasts() {
  toastTimeouts.forEach((timeout) => {
    clearTimeout(timeout);
  });
  toastTimeouts.clear();
  toastDismissTimeouts.forEach((timeout) => {
    clearTimeout(timeout);
  });
  toastDismissTimeouts.clear();
  dispatch({ type: 'REMOVE_TOAST' });
}

type Toast = Omit<ToasterToast, 'id'>;

function toast({ ...props }: Toast) {
  const id = genId();
  const normalizedToast = {
    ...normalizeToastDuration(props),
    id,
    open: true,
    onOpenChange: (open: boolean) => {
      if (!open) dismiss();
    },
  };

  const update = (props: ToasterToast) =>
    dispatch({
      type: 'UPDATE_TOAST',
      toast: { ...props, id },
    });
  const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id });

  dispatch({
    type: 'ADD_TOAST',
    toast: normalizedToast,
  });
  scheduleAutoDismiss(normalizedToast);

  return {
    id: id,
    dismiss,
    update,
  };
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: 'DISMISS_TOAST', toastId }),
  };
}

export { useToast, toast };
